import type { components } from "../../../api/generated";
import type {
  CrmOrganizationUnitDeleteResult,
  CrmOrganizationUnitItem,
  CrmOrganizationUnitListParams,
  CrmOrganizationUnitListPage,
  CrmOrganizationUnitMutation,
  CrmOrganizationUnitTreeNode,
} from "../types";

export type CrmOrganizationUnitItemRaw = components["schemas"]["CrmOrganizationUnitItem"];
export type CrmOrganizationUnitListPageRaw =
  components["schemas"]["CrmOrganizationUnitListPage"];
export type CrmOrganizationUnitMutationRaw =
  components["schemas"]["CrmOrganizationUnitMutation"];
export type CrmOrganizationUnitDeleteResultRaw =
  components["schemas"]["CrmOrganizationUnitDeleteResult"];
export type CrmOrganizationUnitTreeNodeRaw = components["schemas"]["CrmOrganizationUnitTreeNode"];

/** 映射组织单元列表查询参数。 */
export function mapCrmOrganizationUnitListParams(params: CrmOrganizationUnitListParams) {
  return {
    page: params.page,
    page_size: params.pageSize,
    province: normalizeOptionalText(params.province),
    city: normalizeOptionalText(params.city),
    school_keyword: normalizeOptionalText(params.schoolKeyword),
    school_spell_code: normalizeOptionalText(params.schoolSpellCode),
    unit_keyword: normalizeOptionalText(params.unitKeyword),
    unit_type: params.unitType,
    is_disabled: params.isDisabled,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 映射组织单元保存载荷。 */
export function mapCrmOrganizationUnitMutationPayload(
  payload: CrmOrganizationUnitMutation,
): CrmOrganizationUnitMutationRaw {
  return {
    org_id: payload.orgId,
    parent_unit_id: payload.parentUnitId ?? null,
    unit_type: payload.unitType,
    unit_name: payload.unitName.trim(),
    org_status: payload.orgStatus,
    effective_from: normalizeOptionalText(payload.effectiveFrom) ?? null,
    effective_to: normalizeOptionalText(payload.effectiveTo) ?? null,
    is_disabled: payload.isDisabled,
    remark: normalizeOptionalText(payload.remark),
  };
}

/** 映射组织单元分页响应。 */
export function mapCrmOrganizationUnitListPage(
  raw: CrmOrganizationUnitListPageRaw,
): CrmOrganizationUnitListPage {
  return {
    items: (raw.items ?? []).map(mapCrmOrganizationUnitItem),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射组织单元响应项。 */
export function mapCrmOrganizationUnitItem(raw: CrmOrganizationUnitItemRaw): CrmOrganizationUnitItem {
  return {
    id: raw.id,
    legacyId: raw.legacy_id,
    orgId: raw.org_id,
    schoolName: raw.school_name,
    schoolSpellCode: raw.school_spell_code,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    parentUnitId: raw.parent_unit_id ?? undefined,
    unitType: raw.unit_type,
    unitName: raw.unit_name,
    unitPathName: raw.unit_path_name,
    levelNo: raw.level_no,
    collegeUnitId: raw.college_unit_id ?? undefined,
    departmentUnitId: raw.department_unit_id ?? undefined,
    orgStatus: raw.org_status,
    effectiveFrom: raw.effective_from ?? undefined,
    effectiveTo: raw.effective_to ?? undefined,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    contactCount: raw.contact_count,
    customerCount: raw.customer_count,
    childCount: raw.child_count,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
  };
}

/** 映射组织单元删除结果。 */
export function mapCrmOrganizationUnitDeleteResult(
  raw: CrmOrganizationUnitDeleteResultRaw,
): CrmOrganizationUnitDeleteResult {
  return {
    deleted: raw.deleted,
    blockedReasons: raw.blocked_reasons ?? [],
    contactCount: raw.contact_count,
    customerCount: raw.customer_count,
    childCount: raw.child_count,
  };
}

/** 映射组织单元树节点。 */
export function mapCrmOrganizationUnitTreeNode(
  raw: CrmOrganizationUnitTreeNodeRaw,
): CrmOrganizationUnitTreeNode {
  return {
    id: raw.id,
    title: raw.title,
    unitType: raw.unit_type,
    isSchoolRoot: raw.is_school_root,
    children: (raw.children ?? []).map(mapCrmOrganizationUnitTreeNode),
  };
}

/** 归一化可选文本参数。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
